import React, { useEffect, useState } from "react";
import { FaHistory, FaTimes, FaClipboardList } from "react-icons/fa";
import { getData } from "../../services/FetchNodeservices";

const formatDate = (date) => {
  if (!date) return "-";
  const d = new Date(date);
  return `${d.getDate().toString().padStart(2, "0")}-${(d.getMonth() + 1)
    .toString()
    .padStart(2, "0")}-${d.getFullYear()}`;
};

export default function LeaveHistoryModal({ onClose }) {
  const user = JSON.parse(localStorage.getItem("USER"));
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  const [balance, setBalance] = useState({
    carry_forward: 0,
    cl_sl_remaining: 0,
    pl_annual_remaining: 0,
    total_leaves: 0,
    short_leave: 0,
    half_day: 0,
    full_day: 0,
  });

  const fetchHistory = async () => {
    if (!user?.id) return;
    try {
      setLoading(true);

      const res = await getData(`leave/my-leaves/${user.id}`);
      if (res?.status) setRecords(res.data || []);

      const bal = await getData(`leave/balance/${user.id}`);
      if (bal?.status) setBalance(bal.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line
  }, []);

  const statusColor = {
    Pending: "bg-yellow-100 text-yellow-700 border-yellow-400",
    Approved: "bg-green-100 text-green-700 border-green-400",
    Rejected: "bg-red-100 text-red-700 border-red-400",
  };

  const bucketLabel = (b) => (b === "PL" ? "PL (Annual)" : "CL (Casual)");

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center z-50 p-3"
      onClick={onClose}
    >
      <div
        className="bg-white w-[98%] md:w-[900px] max-h-[88vh] rounded-xl shadow-xl border border-gray-200 flex flex-col relative overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-4 text-gray-600 hover:text-red-500 text-xl"
        >
          <FaTimes />
        </button>

        <div className="px-6 pt-6 pb-4 border-b">
          <div className="flex items-center gap-2">
            <FaHistory className="text-indigo-500" size={22} />
            <h2 className="text-lg font-bold text-gray-800">My Leave History</h2>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Track your leave history and balances.
          </p>
        </div>

        <div className="p-6 overflow-y-auto">
          {/* Balance */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-5">
            <div className="flex items-center gap-2 mb-2">
              <FaClipboardList className="text-blue-700" />
              <h3 className="font-bold text-gray-800">Current Leave Balance</h3>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div className="bg-white border rounded-lg p-3">
                <p className="text-gray-500 text-xs">Carry Forward</p>
                <p className="font-bold">
                  {Number(balance.carry_forward).toFixed(2)}
                </p>
              </div>
              <div className="bg-white border rounded-lg p-3">
                <p className="text-gray-500 text-xs">CL Balance</p>
                <p className="font-bold">
                  {Number(balance.cl_sl_remaining).toFixed(2)}
                </p>
              </div>
              <div className="bg-white border rounded-lg p-3">
                <p className="text-gray-500 text-xs">PL Balance</p>
                <p className="font-bold">
                  {Number(balance.pl_annual_remaining).toFixed(2)}
                </p>
              </div>
              <div className="bg-white border rounded-lg p-3">
                <p className="text-gray-500 text-xs">Total Leaves</p>
                <p className="font-bold">
                  {Number(balance.total_leaves).toFixed(2)}
                </p>
              </div>
            </div>

            <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
              <div className="bg-white border rounded p-2 text-center">
                Short Taken: <b>{balance.short_leave}</b>
              </div>
              <div className="bg-white border rounded p-2 text-center">
                Half Taken: <b>{balance.half_day}</b>
              </div>
              <div className="bg-white border rounded p-2 text-center">
                Full Taken: <b>{balance.full_day}</b>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="hidden md:block overflow-x-auto border rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-gray-100 border-b text-gray-700">
                <tr>
                  <th className="p-2 border-r">Leave Type</th>
                  <th className="p-2 border-r">Bucket</th>
                  <th className="p-2 border-r text-center">Deduction</th>
                  <th className="p-2 border-r">From</th>
                  <th className="p-2 border-r">To</th>
                  <th className="p-2 border-r">Reason</th>
                  <th className="p-2 border-r">Applied On</th>
                  <th className="p-2">Status</th>
                </tr>
              </thead>

              <tbody>
                {records.map((row) => (
                  <tr key={row.id} className="border-b hover:bg-gray-50">
                    <td className="p-2 border-r font-medium">{row.leave_type}</td>
                    <td className="p-2 border-r">
                      <span className="px-2 py-1 rounded border text-xs bg-blue-50 text-blue-700 border-blue-200">
                        {bucketLabel(row.leave_bucket)}
                      </span>
                    </td>

                    {/* ✅ FIXED */}
                    <td className="p-2 border-r text-center font-semibold">
                      {Number(row.deducted_days || 0).toFixed(2)}
                    </td>

                    <td className="p-2 border-r">{formatDate(row.date_from)}</td>
                    <td className="p-2 border-r">{formatDate(row.date_to)}</td>
                    <td className="p-2 border-r text-gray-600">{row.reason}</td>
                    <td className="p-2 border-r text-gray-500">
                      {formatDate(row.created_at?.split?.("T")[0])}
                    </td>
                    <td className="p-2">
                      <span
                        className={`px-2 py-1 rounded border text-xs font-semibold ${
                          statusColor[row.status]
                        }`}
                      >
                        {row.status}
                      </span>
                    </td>
                  </tr>
                ))}

                {records.length === 0 && (
                  <tr>
                    <td colSpan="8" className="p-4 text-center text-gray-500">
                      No leave records found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {records.length === 0 ? (
              <div className="border rounded-lg p-4 text-sm text-center text-gray-500">
                No leave records found.
              </div>
            ) : (
              records.map((row) => {
                return (
                  <div key={row.id} className="border rounded-xl p-4 bg-white shadow-sm">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-bold text-gray-800">{row.leave_type}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {bucketLabel(row.leave_bucket)} • Deduct:{" "}
                          <b>{Number(row.deducted_days || 0).toFixed(2)}</b>
                        </p>
                      </div>
                      <span
                        className={`px-2 py-1 rounded border text-xs font-semibold ${
                          statusColor[row.status]
                        }`}
                      >
                        {row.status}
                      </span>
                    </div>

                    <div className="mt-3 text-xs text-gray-700 grid grid-cols-2 gap-2">
                      <p>
                        <span className="text-gray-500">From:</span> {formatDate(row.date_from)}
                      </p>
                      <p>
                        <span className="text-gray-500">To:</span> {formatDate(row.date_to)}
                      </p>
                      <p className="col-span-2">
                        <span className="text-gray-500">Reason:</span> {row.reason || "-"}
                      </p>
                      <p className="col-span-2 text-gray-500">
                        Applied: {formatDate(row.created_at?.split?.("T")[0])}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {loading && (
            <div className="mt-3 text-xs text-gray-500 text-center">Loading...</div>
          )}
        </div>
      </div>
    </div>
  );
}
